import React, { useState, useEffect } from 'react';
import { Check, ChevronRight, BookOpen, MessageCircle, Cpu, AlertTriangle, Save, Edit, X } from 'lucide-react';
import ArticleSubmissionForm from './ArticleSubmissionForm';
import ArticleClaimExtractor from './ArticleClaimExtractor';
import StepByStepArticleAnalyzer from './StepByStepArticleAnalyzer';

// Helper function to format dates in MM/DD/YYYY format
const formatDate = (dateString) => {
  if (!dateString) return 'Unknown';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      // If the date is not valid, return as is
      return dateString;
    }
    
    // Format as MM/DD/YYYY
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

// Main component for the full article workflow
const ArticleWorkflow = ({ publishers = [], authors = [] }) => {
  // Workflow state
  const [currentStep, setCurrentStep] = useState(1); // 1: Submit, 2: Analyze, 3: Review/Publish
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Article data
  const [articleId, setArticleId] = useState(null);
  const [articleData, setArticleData] = useState(null);
  const [articleContent, setArticleContent] = useState('');
  const [articleClaims, setArticleClaims] = useState([]);
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Editing state
  const [editingField, setEditingField] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    publisher: '',
    author: '',
    date: ''
  });
  
  // Log step changes for debugging
  useEffect(() => {
    console.log("Current workflow step changed to:", currentStep);
  }, [currentStep]);
  
  // Initialize edit form data when articleData changes
  useEffect(() => {
    if (articleData) {
      setEditFormData({
        title: articleData.title || '',
        publisher: articleData.publisher || '',
        author: articleData.author || '',
        date: articleData.publication_date || ''
      });
    }
  }, [articleData]);
  
  // Handle article submission
  const handleSubmitArticle = async (formData) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      console.log('Submitting article with form data:', formData);
      
      const response = await fetch('/api/articles/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit article');
      }
      
      const result = await response.json();
      console.log('Article submitted successfully:', result);
      
      // Store article information
      setArticleId(result.id);
      
      // Determine publisher name
      let publisherName = "Unknown";
      if (formData.publisher_id === 'new_publisher' && formData.new_publisher_name) {
        publisherName = formData.new_publisher_name;
      } else if (formData.publisher_id) {
        const pub = publishers.find(p => p.id.toString() === formData.publisher_id.toString());
        publisherName = pub ? pub.name : "Unknown";
      }
      
      // Determine author name
      let authorName = "Unknown";
      if (formData.author_id === 'new_author' && formData.new_author_name) {
        authorName = formData.new_author_name;
      } else if (formData.author_id) {
        const auth = authors.find(a => a.id.toString() === formData.author_id.toString());
        authorName = auth ? auth.name : "Unknown";
      }
      
      setArticleData({
        id: result.id,
        title: formData.title,
        url: formData.url,
        publisher: publisherName,
        author: authorName,
        publication_date: formData.publication_date
      });
      setArticleContent(formData.content);
      
      setSuccess('Article saved successfully');
      setCurrentStep(2); // Move to analyze step
      return result;
    } catch (error) {
      console.error("Error submitting article:", error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // Handle claims change
  const handleClaimsChange = (claims) => {
    setArticleClaims(claims);
  };
  
  // Function to save all claims
  const saveAllClaims = async () => {
    console.log("Saving claims for article:", articleId);
    
    if (articleId === null) {
      setError('No article ID available');
      return Promise.reject(new Error('No article ID available'));
    }

    if (articleClaims.length === 0) {
      console.log("No claims to save, continuing anyway");
      return Promise.resolve({ success: true });
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      console.log(`Sending POST request to /api/articles/${articleId}/claims`);
      console.log('Claims payload:', JSON.stringify(articleClaims, null, 2));
      
      const response = await fetch(`/api/articles/${articleId}/claims`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ claims: articleClaims })
      });

      console.log('Response status:', response.status, response.statusText);

      if (!response.ok) {
        const responseText = await response.text();
        console.error('Error response body:', responseText);
        
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.error || errorData.details || 'Failed to save claims');
        } catch (jsonError) {
          throw new Error(`Failed to save claims: Server returned ${response.status} ${response.statusText}`);
        }
      }

      // Update claims with server-assigned IDs if needed
      const resultText = await response.text();
      console.log('Success response body:', resultText);
      
      try {
        const result = JSON.parse(resultText);
        console.log('Parsed result:', result);
        
        if (result.claims) {
          console.log('Setting updated claims from server');
          setArticleClaims(result.claims);
        }
        setSuccess('Claims saved successfully');
        return Promise.resolve(result);
      } catch (jsonError) {
        console.error('Could not parse success response as JSON:', jsonError);
        setSuccess('Claims saved successfully');
        return Promise.resolve({ success: true });
      }
    } catch (err) {
      console.error('Error saving claims:', err);
      setError(err.message);
      return Promise.reject(err);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle Claude analysis completion
  const handleAnalysisComplete = (result) => {
    console.log("Analysis complete with result:", result);
    // Extract the analysis data and ratings from the result
    const analysisData = result.analysisData || {};
    
    // Get scores from nested structures if needed
    let overallRating = 0;
    let rhetoricScore = 0;
    let constitutionalAccuracyScore = 0;
    let sourceCredibilityScore = 0;
    let truthfulnessScore = 0;
    let summary = '';
    
    // Check different possible locations for scores
    if (analysisData.overall_assessment) {
      // Scores in overall_assessment object
      overallRating = analysisData.overall_assessment.combined_score || 0;
      rhetoricScore = analysisData.overall_assessment.rhetoric_analysis_score || 0;
      constitutionalAccuracyScore = analysisData.overall_assessment.constitutional_accuracy_score || 0;
      sourceCredibilityScore = analysisData.overall_assessment.source_credibility_score || 0;
      truthfulnessScore = analysisData.overall_assessment.truthfulness_score || 0;
      summary = analysisData.overall_assessment.summary || '';
    } else if (analysisData.structured_analysis && analysisData.structured_analysis.overall_assessment) {
      // Scores in structured_analysis.overall_assessment
      overallRating = analysisData.structured_analysis.overall_assessment.combined_score || 0;
      rhetoricScore = analysisData.structured_analysis.overall_assessment.rhetoric_analysis_score || 0;
      constitutionalAccuracyScore = analysisData.structured_analysis.overall_assessment.constitutional_accuracy_score || 0;
      sourceCredibilityScore = analysisData.structured_analysis.overall_assessment.source_credibility_score || 0;
      truthfulnessScore = analysisData.structured_analysis.overall_assessment.truthfulness_score || 0;
      summary = analysisData.structured_analysis.overall_assessment.summary || '';
    } else {
      // Fallback to top-level properties
      overallRating = analysisData.overall_rating || analysisData.finalScore || 0;
      rhetoricScore = analysisData.rhetoric_score || 0;
      constitutionalAccuracyScore = analysisData.constitutional_accuracy_score || 0;
      sourceCredibilityScore = analysisData.source_credibility_score || 0;
      truthfulnessScore = analysisData.truthfulness_score || 0;
      summary = analysisData.summary || '';
    }
    
    // Ensure scores are numbers and not null/undefined
    overallRating = Number(overallRating) || 0;
    rhetoricScore = Number(rhetoricScore) || 0;
    constitutionalAccuracyScore = Number(constitutionalAccuracyScore) || 0;
    sourceCredibilityScore = Number(sourceCredibilityScore) || 0;
    truthfulnessScore = Number(truthfulnessScore) || 0;
    
    console.log('Extracted scores:', {
      overall_rating: overallRating,
      rhetoric_score: rhetoricScore,
      constitutional_accuracy_score: constitutionalAccuracyScore,
      source_credibility_score: sourceCredibilityScore,
      truthfulness_score: truthfulnessScore,
      summary
    });
    
    // Store the complete analysis result
    setAnalysisResult({
      claims: result.claims || [],
      overall_rating: overallRating,
      rhetoric_score: rhetoricScore,
      constitutional_accuracy_score: constitutionalAccuracyScore,
      source_credibility_score: sourceCredibilityScore,
      truthfulness_score: truthfulnessScore,
      summary: summary,
      // Include any other relevant data
      analysisData: analysisData
    });
    
    // Update claims if new ones were identified
    if (result.claims && result.claims.length > 0) {
      setArticleClaims(result.claims);
    }
    
    // Save claims and move to review step
    saveAllClaims()
      .then(() => {
        setSuccess('Analysis complete! You can now review and publish.');
        setCurrentStep(3); // Move to review/publish step
      })
      .catch(err => {
        console.error("Failed to save claims after analysis:", err);
        // Still proceed to review step even if saving claims fails
        setCurrentStep(3);
      });
  };
  
  // Handle editing field input change
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle saving edited field
  const handleSaveEdit = () => {
    // Update the local articleData state with the edited field
    if (editingField && editFormData) {
      const updatedData = { ...articleData };
      
      switch (editingField) {
        case 'title':
          updatedData.title = editFormData.title;
          break;
        case 'publisher':
          updatedData.publisher = editFormData.publisher;
          break;
        case 'author':
          updatedData.author = editFormData.author;
          break;
        case 'date':
          updatedData.publication_date = editFormData.date;
          break;
        default:
          break;
      }
      
      setArticleData(updatedData);
      setEditingField(null); // Close the edit form
    }
  };
  
  // Handle canceling edit
  const handleCancelEdit = () => {
    // Reset the form data to the current article data
    if (articleData) {
      setEditFormData({
        title: articleData.title || '',
        publisher: articleData.publisher || '',
        author: articleData.author || '',
        date: articleData.publication_date || ''
      });
    }
    setEditingField(null); // Close the edit form
  };
  
  // Handle editing claims
  const handleEditClaim = (index, updatedClaim) => {
    const updatedClaims = [...articleClaims];
    updatedClaims[index] = { ...updatedClaims[index], ...updatedClaim };
    setArticleClaims(updatedClaims);
  };
  
  // Handle adding a new claim in the review step
  const handleAddNewClaim = () => {
    setArticleClaims([...articleClaims, { 
      claim_text: "Enter new claim", 
      rebuttal_text: "Enter rebuttal",
      rhetorical_tactics: [],
      article_id: articleId
    }]);
  };
  
  // Handle publishing the article
  const handlePublishArticle = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      console.log('Publishing article:', articleId);
      
      // First get the current article data to ensure we have all fields
      const getResponse = await fetch(`/api/articles/${articleId}`);
      
      if (!getResponse.ok) {
        throw new Error('Failed to retrieve article data');
      }
      
      const currentArticle = await getResponse.json();
      console.log('Current article data:', currentArticle);
      
      // Extract article data from the response (handles nested structure)
      const articleData = currentArticle.article || currentArticle || {};
      
      // Prepare the update data with only the fields the API expects
      const updateData = {
        // Take base fields from the current article data
        url: articleData.url || '',
        title: articleData.title || '',
        publication_date: articleData.publication_date || '',
        
        // Use analysis result summary if available, otherwise use existing
        summary: analysisResult?.summary || articleData.summary || '',
        
        // Status should be Published
        status: 'Published',
        
        // Analysis scores - use our analysis result if available, otherwise keep existing values
        truthfulness_score: analysisResult?.truthfulness_score || articleData.truthfulness_score || 0,
        constitutional_accuracy_score: analysisResult?.constitutional_accuracy_score || articleData.constitutional_accuracy_score || 0,
        source_credibility_score: analysisResult?.source_credibility_score || articleData.source_credibility_score || 0,
        rhetoric_score: analysisResult?.rhetoric_score || articleData.rhetoric_score || 0,
        overall_rating: analysisResult?.overall_rating || articleData.overall_rating || 0,
        
        // Other analysis fields
        primary_fallacies: articleData.primary_fallacies || [],
        democratic_pillars_threatened: articleData.democratic_pillars_threatened || [],
        authoritarian_tactics_employed: articleData.authoritarian_tactics_employed || [],
        analyzed_by: articleData.analyzed_by || 'Claude AI'
      };
      
      console.log('Sending update with data:', updateData);
      
      // Update the article
      const response = await fetch(`/api/articles/${articleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      });
      
      if (!response.ok) {
        let errorMessage = 'Failed to publish article';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // If can't parse JSON, use response text
          errorMessage = await response.text() || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      console.log('Article published successfully:', result);
      
      setSuccess('Article published successfully! It is now live on the site.');
    } catch (error) {
      console.error("Error publishing article:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="max-w-6xl mx-auto">
      {/* Step indicators */}
      <div className="mb-8">
        <nav className="flex items-center justify-between">
          <ol className="flex items-center w-full">
            <li className={`flex items-center ${currentStep >= 1 ? 'text-blue-600' : 'text-gray-500'}`}>
              <span className={`flex items-center justify-center w-8 h-8 rounded-full ${currentStep >= 1 ? 'bg-blue-100' : 'bg-gray-100'}`}>
                {currentStep > 1 ? <Check className="w-5 h-5" /> : <BookOpen className="w-5 h-5" />}
              </span>
              <span className="ml-2 text-sm font-medium">Submit Article</span>
            </li>
            <li className="flex-auto border-t-2 mx-4"></li>
            <li className={`flex items-center ${currentStep >= 2 ? 'text-blue-600' : 'text-gray-500'}`}>
              <span className={`flex items-center justify-center w-8 h-8 rounded-full ${currentStep >= 2 ? 'bg-blue-100' : 'bg-gray-100'}`}>
                {currentStep > 2 ? <Check className="w-5 h-5" /> : <Cpu className="w-5 h-5" />}
              </span>
              <span className="ml-2 text-sm font-medium">Analyze Content</span>
            </li>
            <li className="flex-auto border-t-2 mx-4"></li>
            <li className={`flex items-center ${currentStep >= 3 ? 'text-blue-600' : 'text-gray-500'}`}>
              <span className={`flex items-center justify-center w-8 h-8 rounded-full ${currentStep >= 3 ? 'bg-blue-100' : 'bg-gray-100'}`}>
                <Save className="w-5 h-5" />
              </span>
              <span className="ml-2 text-sm font-medium">Publish</span>
            </li>
          </ol>
        </nav>
      </div>
      
      {/* Error and success messages */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 mr-2" />
            <p>{error}</p>
          </div>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 mb-4">
          <div className="flex">
            <Check className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
            <p>{success}</p>
          </div>
        </div>
      )}
      
      {/* Step 1: Submit Article */}
      {currentStep === 1 && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">Step 1: Submit an Article</h2>
          </div>
          
          <ArticleSubmissionForm 
            onSubmit={handleSubmitArticle}
            publishers={publishers}
            authors={authors}
            hideSubmitButton={false}
            formId="article-form"
          />
        </div>
      )}
      
      {/* Step 2: Analyze Content - Combined Extract Claims and Analyze */}
      {currentStep === 2 && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">Step 2: Analyze Content</h2>
          </div>
          
          <div className="flex justify-end mb-4">
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentStep(1)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Back
              </button>
            </div>
          </div>

          {articleData && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-medium text-blue-800">Article Information</h3>
              <p className="text-sm text-blue-700">
                <span className="font-medium">Title:</span> {articleData.title}
              </p>
              <p className="text-sm text-blue-700">
                <span className="font-medium">Publisher:</span> {articleData.publisher || 'Unknown'}
              </p>
              <p className="text-sm text-blue-700">
                <span className="font-medium">Author:</span> {articleData.author || 'Unknown'}
              </p>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left side: Extract Claims */}
            <div className="bg-gray-50 p-4 rounded-md">
              <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
                <MessageCircle className="h-5 w-5 mr-2 text-blue-500" />
                Extract Claims
              </h3>
              
              <ArticleClaimExtractor
                articleId={articleId}
                articleContent={articleContent}
                onClaimsChange={handleClaimsChange}
                existingClaims={articleClaims}
                isWorkflow={true}
              />
            </div>
            
            {/* Right side: Claude Analysis */}
            <div className="bg-gray-50 p-4 rounded-md">
              <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
                <Cpu className="h-5 w-5 mr-2 text-indigo-500" />
                Analyze with Claude AI
              </h3>
              
              <StepByStepArticleAnalyzer
                articleId={articleId}
                articleContent={articleContent}
                onAnalysisComplete={handleAnalysisComplete}
                existingClaims={articleClaims}
                isWorkflow={true}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Step 3: Review and Publish */}
      {currentStep === 3 && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">Step 3: Review and Publish</h2>
            
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentStep(2)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handlePublishArticle}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                disabled={loading}
              >
                {loading ? 'Publishing...' : 'Publish Article Live'}
              </button>
            </div>
          </div>
          
          <div className="mb-6 border border-green-200 rounded-lg p-4 bg-green-50">
            <h3 className="text-lg font-medium text-green-800 mb-2">Article Ready to Publish</h3>
            <p className="text-sm text-green-700 mb-4">
              The article has been fully analyzed and is ready to be published to the live site.
            </p>
            
            {/* Show claims with their ratings */}
            <div className="mt-4 mb-2">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-green-800">Claims Analysis</h4>
                <button 
                  onClick={handleAddNewClaim}
                  className="text-xs bg-blue-600 text-white px-2 py-1 rounded flex items-center"
                >
                  <span className="mr-1">+</span> Add New Claim
                </button>
              </div>
              <div className="max-h-60 overflow-y-auto bg-white p-3 rounded border border-green-100">
                {articleClaims.map((claim, index) => (
                  <div key={index} className="mb-3 pb-2 border-b border-green-50">
                    <div className="flex justify-between">
                      <div className="font-medium text-sm">{claim.claim_text}</div>
                      <button 
                        onClick={() => setEditingField(`claim-${index}`)} 
                        className="text-blue-600 text-xs hover:underline ml-2 flex items-center"
                      >
                        <Edit className="w-3 h-3 mr-1" /> Edit
                      </button>
                    </div>
                    
                    {editingField === `claim-${index}` ? (
                      <div className="mt-2 bg-blue-50 p-2 rounded">
                        <div className="mb-2">
                          <label className="text-xs font-medium block mb-1">Claim Text</label>
                          <textarea 
                            value={claim.claim_text}
                            onChange={(e) => handleEditClaim(index, { claim_text: e.target.value })}
                            className="w-full text-sm border border-gray-300 rounded p-1 mb-2"
                            rows="2"
                          />
                        </div>
                        <div className="mb-2">
                          <label className="text-xs font-medium block mb-1">Rebuttal</label>
                          <textarea 
                            value={claim.rebuttal_text}
                            onChange={(e) => handleEditClaim(index, { rebuttal_text: e.target.value })}
                            className="w-full text-sm border border-gray-300 rounded p-1"
                            rows="2"
                          />
                        </div>
                        <div className="flex justify-end mt-2">
                          <button 
                            onClick={() => setEditingField(null)}
                            className="text-xs bg-blue-600 text-white px-2 py-1 rounded"
                          >
                            Done Editing
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="text-xs text-gray-600 mt-1"><span className="font-medium">Rebuttal:</span> {claim.rebuttal_text}</div>
                        {claim.rhetorical_tactics && claim.rhetorical_tactics.length > 0 && (
                          <div className="text-xs text-gray-600 mt-1">
                            <span className="font-medium">Tactics:</span> {claim.rhetorical_tactics.map(t => t.name).join(', ')}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-green-800 mb-1">Article Details</h4>
                <ul className="text-sm space-y-1">
                  <li className="flex justify-between items-center">
                    {editingField === 'title' ? (
                      <div className="flex items-center w-full">
                        <input 
                          type="text" 
                          name="title" 
                          value={editFormData.title} 
                          onChange={handleEditChange}
                          className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                        <button onClick={handleSaveEdit} className="text-green-600 ml-2">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={handleCancelEdit} className="text-red-600 ml-2">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span><span className="font-medium">Title:</span> {articleData?.title}</span>
                        <button 
                          onClick={() => setEditingField('title')} 
                          className="text-blue-600 text-xs hover:underline ml-2 flex items-center"
                        >
                          <Edit className="w-3 h-3 mr-1" /> Edit
                        </button>
                      </>
                    )}
                  </li>
                  
                  <li className="flex justify-between items-center">
                    {editingField === 'publisher' ? (
                      <div className="flex items-center w-full">
                        <input 
                          type="text" 
                          name="publisher" 
                          value={editFormData.publisher} 
                          onChange={handleEditChange}
                          className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                        <button onClick={handleSaveEdit} className="text-green-600 ml-2">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={handleCancelEdit} className="text-red-600 ml-2">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span><span className="font-medium">Publisher:</span> {articleData?.publisher || 'Unknown'}</span>
                        <button 
                          onClick={() => setEditingField('publisher')} 
                          className="text-blue-600 text-xs hover:underline ml-2 flex items-center"
                        >
                          <Edit className="w-3 h-3 mr-1" /> Edit
                        </button>
                      </>
                    )}
                  </li>
                  
                  <li className="flex justify-between items-center">
                    {editingField === 'author' ? (
                      <div className="flex items-center w-full">
                        <input 
                          type="text" 
                          name="author" 
                          value={editFormData.author} 
                          onChange={handleEditChange}
                          className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                        <button onClick={handleSaveEdit} className="text-green-600 ml-2">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={handleCancelEdit} className="text-red-600 ml-2">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span><span className="font-medium">Author:</span> {articleData?.author || 'Unknown'}</span>
                        <button 
                          onClick={() => setEditingField('author')} 
                          className="text-blue-600 text-xs hover:underline ml-2 flex items-center"
                        >
                          <Edit className="w-3 h-3 mr-1" /> Edit
                        </button>
                      </>
                    )}
                  </li>
                  
                  <li className="flex justify-between items-center">
                    {editingField === 'date' ? (
                      <div className="flex items-center w-full">
                        <input 
                          type="text" 
                          name="date" 
                          value={editFormData.date} 
                          onChange={handleEditChange}
                          placeholder="MM/DD/YYYY"
                          className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                        <button onClick={handleSaveEdit} className="text-green-600 ml-2">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={handleCancelEdit} className="text-red-600 ml-2">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span><span className="font-medium">Publication Date:</span> {formatDate(articleData?.publication_date)}</span>
                        <button 
                          onClick={() => setEditingField('date')} 
                          className="text-blue-600 text-xs hover:underline ml-2 flex items-center"
                        >
                          <Edit className="w-3 h-3 mr-1" /> Edit
                        </button>
                      </>
                    )}
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-green-800 mb-1">Analysis Summary</h4>
                <ul className="text-sm space-y-1">
                  <li><span className="font-medium">Claims Extracted:</span> {articleClaims.length}</li>
                  <li><span className="font-medium">Overall Rating:</span> {analysisResult?.overall_rating || 0}/100</li>
                  <li><span className="font-medium">Truthfulness Score:</span> {analysisResult?.truthfulness_score || 0}/100</li>
                  <li><span className="font-medium">Rhetoric Score:</span> {analysisResult?.rhetoric_score || 0}/100</li>
                  <li><span className="font-medium">Constitutional Accuracy:</span> {analysisResult?.constitutional_accuracy_score || 0}/100</li>
                  <li><span className="font-medium">Source Credibility:</span> {analysisResult?.source_credibility_score || 0}/100</li>
                </ul>
              </div>
            </div>
          </div>
          
          <p className="text-sm text-gray-600 italic">
            Once published, the article will be visible on the public-facing website. 
            You can still make edits to the article after publishing by going to the Articles list.
          </p>
          
          <div className="mt-6 flex justify-center">
            <button
              onClick={handlePublishArticle}
              className="px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              disabled={loading}
            >
              {loading ? 'Publishing...' : 'Publish Article Now'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleWorkflow;